import * as THREE from 'three';
import { MeshBVH } from 'three-mesh-bvh';
import { COLLISION_CLEARANCE_FT } from './editMode.constants';

type CollisionGeometry = THREE.BufferGeometry & { boundsTree: MeshBVH };

const collisionGeometryCache = new Map<number, WeakMap<THREE.BufferGeometry, CollisionGeometry>>();
const meshToMesh = new THREE.Matrix4();

/** Insets geometry by clearance margin and builds a BVH tree for fast collision tests. */
function getCollisionGeometry(geometry: THREE.BufferGeometry, clearance: number): CollisionGeometry {
  let cache = collisionGeometryCache.get(clearance);
  if (!cache) {
    cache = new WeakMap();
    collisionGeometryCache.set(clearance, cache);
  }
  let collision = cache.get(geometry);
  if (collision) return collision;

  if (!geometry.boundingBox) {
    geometry.computeBoundingBox();
  }
  const box = geometry.boundingBox!;
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const shrink = (axisSize: number) =>
    axisSize > 0 ? Math.max(0, (axisSize - 2 * clearance) / axisSize) : 1;

  const eroded = geometry.clone();
  eroded.translate(-center.x, -center.y, -center.z);
  eroded.scale(shrink(size.x), shrink(size.y), shrink(size.z));
  eroded.translate(center.x, center.y, center.z);

  collision = eroded as CollisionGeometry;
  collision.boundsTree = new MeshBVH(eroded);
  cache.set(geometry, collision);
  return collision;
}

function getMeshes(object: THREE.Object3D): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = [];
  object.traverse((child) => {
    if ((child as THREE.Mesh).isMesh) {
      meshes.push(child as THREE.Mesh);
    }
  });
  return meshes;
}

/** Checks whether object A and B are identical or share an ancestor/descendant relationship. */
function isRelated(a: THREE.Object3D, b: THREE.Object3D): boolean {
  if (a === b) return true;
  for (let node = a.parent; node; node = node.parent) {
    if (node === b) return true;
  }
  for (let node = b.parent; node; node = node.parent) {
    if (node === a) return true;
  }
  return false;
}

/** Checks if an object and all its ancestors are visible. */
export function isEffectivelyVisible(object: THREE.Object3D): boolean {
  for (let node: THREE.Object3D | null = object; node; node = node.parent) {
    if (!node.visible) return false;
  }
  return true;
}

function getShrunkWorldBox(object: THREE.Object3D, clearance: number): THREE.Box3 {
  object.updateMatrixWorld(true);
  return new THREE.Box3().setFromObject(object).expandByScalar(-clearance);
}

function meshesIntersect(meshA: THREE.Mesh, meshB: THREE.Mesh, clearance: number): boolean {
  const geometryA = getCollisionGeometry(meshA.geometry, clearance);
  const geometryB = getCollisionGeometry(meshB.geometry, clearance);
  meshToMesh.copy(meshA.matrixWorld).invert().multiply(meshB.matrixWorld);
  return geometryA.boundsTree.intersectsGeometry(geometryB, meshToMesh);
}

interface CollisionCandidate {
  object: THREE.Object3D;
  box: THREE.Box3;
  meshes: THREE.Mesh[];
}

export interface CollisionContext {
  candidates: CollisionCandidate[];
  clearance: number;
}

/** Prepares static collision candidates once per edit session. */
export function createCollisionContext(
  movingPart: THREE.Object3D,
  candidates: THREE.Object3D[],
  clearance: number = COLLISION_CLEARANCE_FT,
): CollisionContext {
  const prepared = candidates
    .filter((candidate) => isEffectivelyVisible(candidate) && !isRelated(candidate, movingPart))
    .map((object) => ({ object, box: getShrunkWorldBox(object, clearance), meshes: getMeshes(object) }))
    .filter((candidate) => !candidate.box.isEmpty() && candidate.meshes.length > 0);

  return { candidates: prepared, clearance };
}

function collidesWith(
  movingBox: THREE.Box3,
  movingMeshes: THREE.Mesh[],
  candidate: CollisionCandidate,
  clearance: number,
): boolean {
  if (!movingBox.intersectsBox(candidate.box)) {
    return false;
  }
  return movingMeshes.some((meshA) => candidate.meshes.some((meshB) => meshesIntersect(meshA, meshB, clearance)));
}

/** Finds all objects in the collision context that intersect the moving part. */
export function findIntersectingParts(movingPart: THREE.Object3D, context: CollisionContext): THREE.Object3D[] {
  const movingBox = getShrunkWorldBox(movingPart, context.clearance);
  if (movingBox.isEmpty()) return [];
  const movingMeshes = getMeshes(movingPart);
  return context.candidates
    .filter((candidate) => collidesWith(movingBox, movingMeshes, candidate, context.clearance))
    .map((candidate) => candidate.object);
}

/** Checks if the moving part intersects any candidate not in ignoredUUIDs. */
export function hasIntersection(
  movingPart: THREE.Object3D,
  context: CollisionContext,
  ignoredUUIDs: Set<string>,
): boolean {
  const movingBox = getShrunkWorldBox(movingPart, context.clearance);
  if (movingBox.isEmpty()) return false;
  const movingMeshes = getMeshes(movingPart);
  return context.candidates.some((candidate) => (
    !ignoredUUIDs.has(candidate.object.uuid) && collidesWith(movingBox, movingMeshes, candidate, context.clearance)
  ));
}
